import { useEffect, useRef, useState } from 'react';
import { creatorOrderV2Api } from '@/apis/creatorOrderV2/creatorOrderV2Api';
import type {
  CreatorQuotePreviewRequest,
  CreatorQuotePreviewResponse
} from '@/interface/creatorOrderV2';

type PreviewState = {
  key: string;
  status: 'loading' | 'success' | 'error';
  data: CreatorQuotePreviewResponse | null;
  error: unknown;
};

const isValidPreview = (data: CreatorQuotePreviewResponse, orderId: string) =>
  data?.orderId === orderId &&
  [
    data.amount,
    data.listTotal,
    data.discountAmount,
    data.discountRate,
    data.serviceFee,
    data.serviceFeeRate,
    data.subsidyAmount
  ].every((value) => typeof value === 'number' && Number.isFinite(value));

export default function useCreatorQuotePreview({
  orderId,
  request,
  enabled,
  debounceMs = 250
}: {
  orderId: string;
  request: CreatorQuotePreviewRequest | null;
  enabled: boolean;
  debounceMs?: number;
}) {
  const [state, setState] = useState<PreviewState | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const sequenceRef = useRef(0);
  const requestKey = request ? JSON.stringify(request) : '';
  const key = `${orderId}:${requestKey}:${retryCount}`;
  const canRequest = enabled && Boolean(orderId && requestKey);

  useEffect(() => {
    if (!canRequest) {
      setState(null);
      return undefined;
    }

    let active = true;
    const sequence = ++sequenceRef.current;
    const body = JSON.parse(requestKey) as CreatorQuotePreviewRequest;
    setState({ key, status: 'loading', data: null, error: null });
    const timer = window.setTimeout(async () => {
      try {
        const result = await creatorOrderV2Api.quotePreview(orderId, body);
        if (!active || sequence !== sequenceRef.current) return;
        if (!isValidPreview(result.data, orderId)) {
          throw new Error('Quote preview returned invalid pricing details.');
        }
        setState({ key, status: 'success', data: result.data, error: null });
      } catch (error) {
        if (!active || sequence !== sequenceRef.current) return;
        setState({ key, status: 'error', data: null, error });
      }
    }, debounceMs);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [canRequest, debounceMs, key, orderId, requestKey]);

  const retry = () => setRetryCount((count) => count + 1);
  if (!canRequest)
    return { status: 'idle' as const, data: null, error: null, retry };
  return state?.key === key
    ? { ...state, retry }
    : { status: 'loading' as const, data: null, error: null, retry };
}
